import { useAtom } from 'jotai';
import { useCallback, useRef } from 'react';
import {
  FormFieldRegistry,
  ValidatedFieldAtoms,
  validateFieldAtom,
} from './formAtoms';
import { validateField } from './formValidation';

export type UseJotaiFormConfig = {
  onSubmit?: (isValid: boolean) => void | Promise<void>;
};

/**
 * Hook for managing form state with Jotai atoms
 * Provides validation, error handling, and form submission
 */
export const useJotaiForm = (config?: UseJotaiFormConfig) => {
  const registryRef = useRef(new FormFieldRegistry());

  const registerField = useCallback(
    <T>(fieldName: string, fieldAtoms: ValidatedFieldAtoms<T>) => {
      registryRef.current.register(fieldName, fieldAtoms);
    },
    []
  );

  const unregisterField = useCallback((fieldName: string) => {
    registryRef.current.unregister(fieldName);
  }, []);

  const validateAllFields = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (
      get: (atom: any) => any,
      set: (atom: any, value: any) => void
    ): boolean => {
      const fields = registryRef.current.getAllFields();
      let allValid = true;

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      for (const [_, fieldAtoms] of Array.from(fields)) {
        const isFieldValid = validateFieldAtom(
          get as any,
          set as any,
          fieldAtoms
        );
        if (!isFieldValid) {
          allValid = false;
        }
      }

      return allValid;
    },
    []
  );

  const handleSubmit = useCallback(
    (get: (atom: any) => any, set: (atom: any, value: any) => void) => {
      return async () => {
        const isValid = validateAllFields(get, set);
        if (config?.onSubmit) {
          await config.onSubmit(isValid);
        }
        return isValid;
      };
    },
    [config, validateAllFields]
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const resetForm = useCallback((set: (atom: any, value: any) => void) => {
    const fields = registryRef.current.getAllFields();

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    for (const [_, fieldAtoms] of Array.from(fields)) {
      set(fieldAtoms.errorAtom, null);
      set(fieldAtoms.touchedAtom, false);
    }
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const clearErrors = useCallback((set: (atom: any, value: any) => void) => {
    const fields = registryRef.current.getAllFields();

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    for (const [_, fieldAtoms] of Array.from(fields)) {
      set(fieldAtoms.errorAtom, null);
    }
  }, []);

  return {
    registerField,
    unregisterField,
    validateAllFields,
    handleSubmit,
    resetForm,
    clearErrors,
  };
};

/**
 * Hook for managing a single validated field
 * Handles value changes, validation on blur, and error display
 */
export const useValidatedField = <T, TWrite = T>(
  fieldAtoms: ValidatedFieldAtoms<T, TWrite>
) => {
  const [value, setValue] = useAtom(fieldAtoms.valueAtom);
  const [error, setError] = useAtom(fieldAtoms.errorAtom);
  const [touched, setTouched] = useAtom(fieldAtoms.touchedAtom);

  const handleChange = useCallback(
    (newValue: T) => {
      setValue(newValue as unknown as TWrite);
      // Clear error on change if field was previously touched
      if (touched && error) {
        setError(null);
      }
    },
    [error, setError, setValue, touched]
  );

  const handleBlur = useCallback(() => {
    setTouched(true);
    if (fieldAtoms.validator) {
      const validationResult = validateField(
        value as any,
        fieldAtoms.validator
      );
      setError(validationResult.errorMessage);
    }
  }, [fieldAtoms.validator, setError, setTouched, value]);

  const validate = useCallback(() => {
    setTouched(true);
    if (fieldAtoms.validator) {
      const validationResult = validateField(
        value as any,
        fieldAtoms.validator
      );
      setError(validationResult.errorMessage);
      return validationResult.isValid;
    }
    return true;
  }, [fieldAtoms.validator, setError, setTouched, value]);

  const showError = touched && error !== null;

  return {
    error,
    handleBlur,
    handleChange,
    setError,
    setTouched,
    setValue,
    showError,
    touched,
    validate,
    value,
  };
};
